import { format, isValid, parseISO } from 'date-fns';
import {
  DEFAULT_CUSTOMER_LOCALE,
  type CustomerDateFormat,
  type CustomerLocale,
  type CustomerNumberFormat,
  parseCustomerLocale
} from './CustomerLocale';

const DATE_FNS_PATTERNS: Record<CustomerDateFormat, string> = {
  UsMonthDayYear: 'MM/dd/yyyy',
  UkDayMonthYear: 'dd/MM/yyyy',
  IsoYearMonthDay: 'yyyy-MM-dd'
};

const NUMBER_FORMAT_CULTURE: Record<CustomerNumberFormat, string> = {
  UsCommaDot: 'en-US',
  EuropeanDotComma: 'de-DE',
  FrenchCanadianSpaceComma: 'fr-CA'
};

export function dateFormatToDateFnsPattern(dateFormat: CustomerDateFormat): string {
  return DATE_FNS_PATTERNS[dateFormat];
}

/** Prefers explicit cultureTag; falls back to a culture derived from numberFormat. */
export function numberFormatToCultureTag(numberFormat: CustomerNumberFormat, cultureTag?: string): string {
  const trimmed = cultureTag?.trim();
  if (trimmed) return trimmed;
  return NUMBER_FORMAT_CULTURE[numberFormat];
}

function toValidDate(value: string | Date): Date | null {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const iso = parseISO(trimmed);
    if (isValid(iso)) return iso;
  } catch {
    // fall through
  }

  const fallback = new Date(trimmed);
  return isValid(fallback) ? fallback : null;
}

export function formatCustomerDate(value: string | Date, locale: CustomerLocale = DEFAULT_CUSTOMER_LOCALE): string {
  const date = toValidDate(value);
  if (!date) {
    return typeof value === 'string' ? value.trim() : '';
  }
  return format(date, dateFormatToDateFnsPattern(locale.dateFormat));
}

export function formatCustomerNumber(
  value: number,
  locale: CustomerLocale = DEFAULT_CUSTOMER_LOCALE,
  options?: Intl.NumberFormatOptions
): string {
  const culture = numberFormatToCultureTag(locale.numberFormat, locale.cultureTag);
  return new Intl.NumberFormat(culture, options).format(value);
}

/**
 * Resolves cultureTag for Intl currency formatting from a document context bag.
 * Prefer `customerLocale.cultureTag`; when absent, use optional `fallbackCultureTag`
 * (org viewer locale — same seam as `resolveDocumentCurrency`'s org currency fallback).
 */
export function resolveContextCultureTag(context: Record<string, unknown> | null | undefined, fallbackCultureTag?: string | null): string {
  if (context) {
    const parsed = parseCustomerLocale(context.customerLocale);
    if (parsed?.cultureTag) return parsed.cultureTag;
  }

  const fallback = fallbackCultureTag?.trim();
  if (fallback) return fallback;

  return DEFAULT_CUSTOMER_LOCALE.cultureTag;
}

/** Resolves a validated CustomerLocale from context, or the US default. */
export function resolveContextCustomerLocale(context: Record<string, unknown> | null | undefined): CustomerLocale {
  if (!context) return DEFAULT_CUSTOMER_LOCALE;
  return parseCustomerLocale(context.customerLocale) ?? DEFAULT_CUSTOMER_LOCALE;
}
